/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import {
  claudeCliLink, claudeLocalCommand, claudeMcpCommand, claudePluginCommand, codexAddCommand,
  codexLocalCommand, codexOpenLink, codexPluginCommand, cursorInstallLink, cursorLocalInstallLink,
  cursorSnippet, mcpServersLocalSnippet, opencodeLocalSnippet, opencodeSnippet, pluginName, shellArg,
} from './links';
import {
  CLIENT_LABELS, MCP_CLIENT_IDS,
  type ClientOverride, type EditorGroup, type EditorTab, type McpClientId,
  type McpServerDescriptor,
} from './types';

const CONNECT = '1-Click Connect';
const CODEX_HINT =
  'Prefills the command in a new Codex chat — press Enter, approve it, then ' +
  'click the sign-in link Codex posts.';
/** Local servers have no /mcp or OAuth step: the client spawns the process,
 * and the server runs its own sign-in when a tool first needs an account. */
const LOCAL_NOTE =
  'Runs on this machine — your client starts it, nothing is hosted. There is no sign-in step here: ' +
  'the first tool call that needs an account runs the server’s own sign-in in your browser. Never paste a token.';
const LOCAL_COPIED = '✓ copied — loads in your next session';

function template(id: McpClientId, s: McpServerDescriptor): EditorTab {
  const { name, title, transport, plugin } = s;
  const local = transport.kind === 'local' ? transport : undefined;
  const url = transport.kind === 'remote' ? transport.url : '';
  const docs = s.docLink ? [s.docLink] : [];
  const desktopExit = s.clients.includes('claude-desktop')
    ? 'If nothing opens: run the command above if you have Claude Code, or switch to Claude Desktop above.'
    : 'If nothing opens: run the command above.';
  const addPrompt = (cmd: string) => `Add the ${title} MCP connector by running:\n${cmd}`;

  switch (id) {
    case 'claude-code': {
      const add = local ? claudeLocalCommand(name, local) : claudeMcpCommand(name, url);
      const mcpOnly = {
        body: plugin
          ? `No marketplace access needed — the connector on its own, without the plugin's skills and agents. Pick this if the plugin install fails, or you only want the tools.`
          : `Adds the ${title} connector to Claude Code for your user, in every project.`,
        snippet: add,
        copiedLine: local ? LOCAL_COPIED : '✓ copied — then: /mcp to sign in',
        connect: {
          label: CONNECT,
          href: claudeCliLink(addPrompt(add)),
          hint: local
            ? 'Prefills the add-connector command — press Enter and approve it.'
            : 'Prefills the add-connector command — press Enter, approve it, then /mcp to sign in.',
        },
        note: plugin
          ? `Needs Claude Code to have run once, same as the plugin path. ${local ? LOCAL_NOTE : 'Run /mcp afterwards to sign in.'} Skills and agents stay with the plugin.`
          : `1-Click Connect needs Claude Code to have run once — the link handler registers on first use. ${desktopExit} ${local ? LOCAL_NOTE : 'Run /mcp afterwards to sign in.'}`,
        links: docs,
      };
      const base: EditorTab = { id, label: CLIENT_LABELS[id], chromeTitle: 'terminal', prompt: '$ ', ...mcpOnly };
      if (!plugin) return base;
      const cmd = claudePluginCommand(plugin);
      return {
        ...base,
        body: `The ${pluginName(plugin)} plugin adds the ${title} connector plus the plugin's skills and agents. Install once, then just ask.`,
        snippet: cmd,
        copiedLine: local ? LOCAL_COPIED : '✓ copied — then: /mcp to sign in',
        connect: {
          label: CONNECT,
          href: claudeCliLink(`Install the ${pluginName(plugin)} plugin by running:\n${cmd}`),
          hint: local
            ? 'Prefills the command in a new Claude Code session — press Enter and approve it.'
            : 'Prefills the command in a new Claude Code session — press Enter, approve it, then /mcp to sign in.',
        },
        note: `1-Click Connect needs Claude Code to have run once — the link handler registers on first use. ${desktopExit}${local ? ` ${LOCAL_NOTE}` : ''}`,
        links: [...(plugin.guideUrl ? [{ label: 'Plugin guide', href: plugin.guideUrl }] : []), ...docs],
        variants: [
          { id: 'plugin', label: 'All-in-one Plugin', overrides: {} },
          { id: 'mcp-only', label: 'Only MCP', overrides: mcpOnly },
        ],
      };
    }
    case 'claude-desktop':
      if (local) {
        return {
          id, label: CLIENT_LABELS[id],
          body: 'Add custom connector takes remote URLs only, so this is a config-file install: open Settings → Developer → Edit Config, merge the block shown here into claude_desktop_config.json, then quit and reopen Claude Desktop.',
          snippet: mcpServersLocalSnippet(name, local), chromeTitle: 'claude_desktop_config.json', prompt: '',
          copiedLine: '✓ copied — merge into claude_desktop_config.json, then restart',
          note: `${LOCAL_NOTE} Claude Desktop starts it without your shell profile — if it fails to start, put the full path from \`which ${local.command}\` in "command".`,
          links: docs,
        };
      }
      return {
        id, label: CLIENT_LABELS[id],
        body: 'Open Customize → Connectors → Add custom connector and paste the URL shown here. Claude Desktop and claude.ai share one Connectors list, so adding it once covers both.',
        snippet: url, chromeTitle: 'Customize → Connectors', prompt: '',
        copiedLine: '✓ copied — paste into Add custom connector',
        note: 'On Team or Enterprise plans only an Owner can add a connector — choose “No client ID — register one automatically”.',
        links: docs,
      };
    case 'cursor':
      if (local) {
        return {
          id, label: CLIENT_LABELS[id],
          body: 'Cursor has no install command — use 1-Click Connect, or merge the config shown here into ~/.cursor/mcp.json (Settings → Tools & MCP adds it too).',
          snippet: mcpServersLocalSnippet(name, local), chromeTitle: '~/.cursor/mcp.json', prompt: '',
          copiedLine: '✓ copied — merge into ~/.cursor/mcp.json',
          connect: {
            label: CONNECT, href: cursorLocalInstallLink(name, local),
            hint: 'Opens Cursor’s install prompt (needs Cursor installed) — confirm there and Cursor starts the server.',
          },
          note: LOCAL_NOTE,
          links: docs,
        };
      }
      return {
        id, label: CLIENT_LABELS[id],
        body: 'Cursor has no install command — use 1-Click Connect, or merge the config shown here into ~/.cursor/mcp.json (Settings → Tools & MCP adds it too).',
        snippet: cursorSnippet(name, url), chromeTitle: '~/.cursor/mcp.json', prompt: '',
        copiedLine: '✓ copied — merge into ~/.cursor/mcp.json',
        connect: {
          label: CONNECT, href: cursorInstallLink(name, url),
          hint: 'Opens Cursor’s install prompt (needs Cursor installed) — confirm there, then sign in.',
        },
        links: docs,
      };
    case 'opencode':
      if (local) {
        return {
          id, label: CLIENT_LABELS[id],
          body: 'Merge the config shown here into ~/.config/opencode/opencode.jsonc (or a project opencode.json). OpenCode starts the server when a session opens.',
          snippet: opencodeLocalSnippet(name, local), chromeTitle: '~/.config/opencode/opencode.jsonc', prompt: '',
          copiedLine: '✓ copied — merge into opencode.jsonc',
          note: LOCAL_NOTE,
          links: docs,
        };
      }
      return {
        id, label: CLIENT_LABELS[id],
        body: `Merge the config shown here into ~/.config/opencode/opencode.jsonc (or a project opencode.json), then sign in with opencode mcp auth ${shellArg(name)}.`,
        snippet: opencodeSnippet(name, url), chromeTitle: '~/.config/opencode/opencode.jsonc', prompt: '',
        copiedLine: `✓ copied — then: opencode mcp auth ${shellArg(name)}`,
        links: docs,
      };
    case 'codex': {
      const add = local ? codexLocalCommand(name, local) : codexAddCommand(name, url);
      const localHint = 'Prefills the command in a new Codex chat — press Enter and approve it.';
      const mcpOnly = local ? {
        body: plugin
          ? 'No marketplace access needed — the connector on its own. Pick this if the plugin install fails.'
          : `Adds the ${title} connector to Codex; Codex starts it on this machine.`,
        snippet: add,
        copiedLine: LOCAL_COPIED,
        connect: { label: CONNECT, href: codexOpenLink(addPrompt(add)), hint: localHint },
        links: docs,
      } : {
        body: plugin
          ? 'No marketplace access needed — the connector on its own. Pick this if the plugin install fails. The add signs you in on the spot: in a terminal your browser opens; through 1-Click Connect, Codex posts the sign-in link in the chat.'
          : 'The add signs you in on the spot: in a terminal your browser opens; through 1-Click Connect, Codex posts the sign-in link in the chat.',
        snippet: add,
        copiedLine: '✓ copied — sign-in opens in your browser',
        connect: { label: CONNECT, href: codexOpenLink(addPrompt(add)), hint: CODEX_HINT },
        links: docs,
      };
      const note = `The Codex CLI, IDE extension and ChatGPT desktop app share one config (~/.codex/config.toml), so installing once covers all three.${local ? ` ${LOCAL_NOTE}` : ''}`;
      const base: EditorTab = { id, label: CLIENT_LABELS[id], chromeTitle: 'terminal', prompt: '$ ', note, ...mcpOnly };
      if (!plugin) return base;
      const cmd = codexPluginCommand(plugin);
      return {
        ...base,
        body: local
          ? `Codex installs the same ${pluginName(plugin)} plugin — the connector plus its skills.`
          : `Codex installs the same ${pluginName(plugin)} plugin — the connector plus its skills. Install, then sign in with codex mcp login ${shellArg(name)}.`,
        snippet: cmd,
        copiedLine: local ? LOCAL_COPIED : `✓ copied — then: codex mcp login ${shellArg(name)}`,
        connect: {
          label: CONNECT,
          href: codexOpenLink(local
            ? `Install the ${pluginName(plugin)} plugin by running:\n${cmd}`
            : `Install the ${pluginName(plugin)} plugin by running:\n${cmd} && codex mcp login ${shellArg(name)}`),
          hint: local ? localHint : CODEX_HINT,
        },
        links: [...(plugin.guideUrl ? [{ label: 'Plugin guide', href: plugin.guideUrl }] : []), ...docs],
        variants: [
          { id: 'plugin', label: 'All-in-one Plugin', overrides: {} },
          { id: 'mcp-only', label: 'Only MCP', overrides: mcpOnly },
        ],
      };
    }
    default:
      throw new Error(`unknown client ${id satisfies never}`);
  }
}

function applyOverride(tab: EditorTab, override?: ClientOverride): EditorTab {
  if (!override) return tab;
  const { variants, ...fields } = override;
  return {
    ...tab,
    ...fields,
    variants: tab.variants?.map(v => ({ ...v, overrides: { ...v.overrides, ...variants?.[v.id] } })),
  };
}

export function buildClients(server: McpServerDescriptor) {
  const requires = server.transport.kind === 'local' ? server.transport.requires : undefined;
  const clients = MCP_CLIENT_IDS.filter(id => server.clients.includes(id)).map(id => {
    const tab = template(id, server);
    return applyOverride(requires ? { ...tab, requires } : tab, server.overrides?.[id]);
  });
  const claude = clients.filter(c => c.id === 'claude-code' || c.id === 'claude-desktop');
  const groups: EditorGroup[] = [
    ...(claude.length ? [{ id: 'claude', label: 'Claude', clients: claude }] : []),
    ...clients.filter(c => !claude.includes(c)).map(c => ({ id: c.id, label: c.label, clients: [c] })),
  ];
  return { clients, groups };
}
